// ── Bookings overview: the first thing on the Bookings page (6 Oct) ──────────
//
// Manny: the Bookings page should open on a high-level view of everything
// coming up, like My journal on My Workbench. One list across drop-offs, fits
// and rentals, read from /book/overview/staff:
//   - today at a glance, and what needs someone's attention
//   - the next two weeks, day by day
// A row opens that booking on its own tab.
//
// Built from the same pieces as My journal (effortUi) so the two read alike.

import React, { useState, useEffect, useCallback } from 'react';
import { API, authHeaders } from '../utils/helpers';
import { dayHeading, colors, Panel, Chip, StatTile, Row, Empty } from './effortUi';

// Light and dark shades: the light ones are too dim on the dark theme.
const EVENT = {
  dropoff: { label: 'Drop-off', color: '#2b6cb0', dark: '#6cb4ee' },
  fit: { label: 'Fitting', color: '#8e44ad', dark: '#c39bd3' },
  pickup: { label: 'Pick-up', color: '#c0661a', dark: '#f0a35e' },
  return: { label: 'Return', color: '#1e8449', dark: '#5dd48a' },
  late: { label: 'Late', color: '#c0392b', dark: '#ff8a7a' },
};
const clock = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

export default function BookingsOverview({ T, isDark, onOpen, onCounts }) {
  const c = colors(isDark);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/book/overview/staff?days=14`, { credentials: 'include', headers: authHeaders() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not load the bookings overview.');
      setData(d); setError('');
      if (onCounts) onCounts(d.counts);
    } catch (err) { setError(err.message); }
  }, [onCounts]);
  // Every two minutes, so a morning left open keeps up with online bookings.
  useEffect(() => { load(); const id = setInterval(load, 120000); return () => clearInterval(id); }, [load]);

  if (!data) return <div role={error ? 'alert' : undefined} style={{ color: error ? '#e74c3c' : T.textSub, padding: 40 }}>{error || 'Loading bookings…'}</div>;

  const { today, events, counts } = data;
  const todays = events.filter(e => e.date === today);
  const later = events.filter(e => e.date > today);
  const days = [...new Set(later.map(e => e.date))];
  const tally = (list) => {
    const n = (ev) => list.filter(e => e.event === ev).length;
    return [
      n('dropoff') && `${n('dropoff')} drop-off${n('dropoff') === 1 ? '' : 's'}`,
      n('fit') && `${n('fit')} fit${n('fit') === 1 ? '' : 's'}`,
      n('pickup') && `${n('pickup')} pick-up${n('pickup') === 1 ? '' : 's'}`,
      n('return') && `${n('return')} return${n('return') === 1 ? '' : 's'}`,
    ].filter(Boolean).join(' · ');
  };
  const up = counts.upcoming || {};

  const row = (e, { showDay = false } = {}) => {
    const ev = EVENT[e.event] || EVENT.dropoff;
    const flags = e.flags || [];
    return (
      <Row key={`${e.event}-${e.id}`} T={T} isDark={isDark} onOpen={() => onOpen(e)} title="Open this booking">
        <span style={{ width: showDay ? 74 : 64, fontSize: 13, fontWeight: 700, color: T.text, fontVariantNumeric: 'tabular-nums', flexShrink: 0, lineHeight: 1.3 }}>
          {showDay && e.event !== 'late' && <span style={{ display: 'block' }}>{new Date(`${e.date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>}
          {e.event === 'late' ? <span style={{ color: c.bad }}>Overdue</span> : e.time ? clock(e.time) : (e.event === 'dropoff' && !showDay ? 'Any time' : '')}
        </span>
        <Chip isDark={isDark} dot={isDark ? ev.dark : ev.color} color={isDark ? ev.dark : ev.color}>{ev.label}{e.demo ? ' · demo' : ''}</Chip>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.customerName}</span>
          <span style={{ fontSize: 13, color: c.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{[e.title, e.detail].filter(Boolean).join(' · ')}</span>
          {flags.map((f, i) => (
            <span key={i} style={{ fontSize: 12.5, fontWeight: 600, color: f.level === 'high' ? c.bad : c.warn }}>{f.level === 'high' ? '⚠ ' : ''}{f.text}</span>
          ))}
        </div>
        {e.workorderId && <span style={{ fontSize: 12, color: c.sub, fontFamily: "'IBM Plex Mono', monospace" }}>#{e.workorderId}</span>}
      </Row>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {error && <div role="alert" style={{ fontSize: 14, color: '#e74c3c' }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <StatTile T={T} label="Today" value={counts.today} sub={tally(todays.filter(e => e.event !== 'late')) || 'nothing booked today'} />
        <StatTile T={T} label="This week" value={counts.week} sub="next 7 days, all bookings" />
        <StatTile T={T} label="Upcoming" value={(up.dropoff || 0) + (up.fitting || 0) + (up.rental || 0)}
          sub={`${up.dropoff || 0} drop-off${up.dropoff === 1 ? '' : 's'} · ${up.fitting || 0} fit${up.fitting === 1 ? '' : 's'} · ${up.rental || 0} rental${up.rental === 1 ? '' : 's'}`} />
        <StatTile T={T} label="Needs attention" value={counts.attention} valueColor={counts.attention ? c.bad : c.good}
          sub={counts.attention ? 'see the list below' : 'nothing waiting on anyone'} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 16, alignItems: 'start' }}>
        <Panel T={T} title="Today" right={dayHeading(today, today)}>
          {todays.length === 0 ? <Empty T={T}>Nothing booked for today.</Empty> : todays.map(e => row(e))}
        </Panel>
        <Panel T={T} title="Needs attention">
          {data.attention.length === 0 ? <Empty T={T}>Nothing needs attention. Late rentals, damage holds not in place, and confirmation texts that did not go out show here.</Empty>
            : events.filter(e => (e.flags || []).some(f => f.level === 'high')).map(e => row(e, { showDay: e.date !== today }))}
        </Panel>
      </div>

      <Panel T={T} title="Coming up" right="next 14 days">
        {days.length === 0 ? <Empty T={T}>Nothing booked in the next two weeks.</Empty>
          : days.map(d => {
            const list = later.filter(e => e.date === d);
            return (
              <div key={d} style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{dayHeading(d, today)}</span>
                  <span style={{ fontSize: 12.5, color: c.sub }}>{tally(list)}</span>
                </div>
                {list.map(e => row(e))}
              </div>
            );
          })}
      </Panel>
    </div>
  );
}
